import { ImmutableState, DeclaredState, FitnessState } from './data-model/user-state';
import { ExerciseIntensities, FitnessPlan } from './data-model/fitness-plan';
import { MuscleCustomizationGroup, OneMuscleGrowthValue } from './data-model/muscle-growth-preference';
import { TrainingLevel, TrainingLevelEnumName, TrainingLevels } from './data-model/training-level';

// note that the numbers in $<exercise>$<num> are 0-based, while those same exercises in the specs (Spreadsheet `Program Optimizer v6.06`) are  listed 1-based!
export type ExerciseId =
  | '$rdl'
  | '$squat'
  | '$squat$1'
  | '$unilateral_squat'
  | '$chin_up'
  | '$pull_up'
  | '$bench_press'
  | '$bench_press$1'
  | '$ohp'
  | '$ohp$4'
  | '$hip_extension'
  | '$lateral$1'
  | '$lateral'
  | '$hams'
  | '$hams$2'
  | '$calves'
  | '$calves$4'
  | '$lats'
  | '$lats$3'
  | '$rear_delt'
  | '$rear_delt$2'
  | '$biceps'
  | '$biceps$4'
  | '$quads'
  | '$triceps'
  | '$triceps$2' //
  | '$glutes'
  | '$glute_medius'
  | '$glute_medius$2'
  | '$chest'
  | '$chest$2'
  | '$soleus'
  | '$upper_traps'
  | '$abs';

export type MuscleId =
  | 'chest'
  | 'ldelt'
  | 'rdelt'
  | 'utraps'
  | 'lats'
  | 'biceps'
  | 'triceps'
  | 'erectors'
  | 'quads'
  | 'hams'
  | 'glutes'
  | 'glutemed'
  | 'gastrocnemius'
  | 'soleus'
  | 'abs';

// The display names for these muscles are NOT here. They are user-visible text,
// so they live in `client/src/assets/i18n/en.json` and are looked up by id in
// `client/src/util/muscle-labels.ts`, where they can be translated (#4210
// Phase E). That lookup is typed by `MuscleId`, so adding a muscle here without
// adding its label does not build.

export type MuscleFactors = {
  [key in MuscleId]?: number;
}

/**
 * Which part of a Push/Pull/Legs program something belongs to (#4591).
 *
 * It is declared here, rather than beside the rest of the training-split code
 * in `workout-split-policy.ts`, only because `MuscleConstants` below needs it
 * and that module already imports from this one.
 */
export type SplitGroup = 'push' | 'pull' | 'legs';

export interface MuscleConstants {
  isUpper: boolean;
  customizeBy: MuscleCustomizationGroup;
  variety: { [key in TrainingLevelEnumName]: number };
  /**
   * Which part of a Push/Pull/Legs program this muscle belongs to (#4591).
   * A slot's own group is worked out from these marks — see
   * `deriveSlotSplitGroup` below. Nothing is tagged per exercise.
   */
  splitGroup: SplitGroup;
}

export type MandatoryBy = 'all' | 'upper' | 'lower' | '$rdl' | '$squat' | '$bench_press' | '$ohp' | '$chin_up';
export type MandatoryWhen = 'never' | 'base' | 'novice' | 'intermediate' | 'advanced'; // "base" is used when mandatory even at low frequencies
export type ExerciseKind = 'big' | 'big$2' | 'aux' | 'aux$2' | 'isolation' | 'isolation$2';

export interface ExerciseConstants {
  kind: ExerciseKind;
  priorityAdjustment: number;
  levelBy: 'upper' | 'lower';
  primeMovers: MuscleId[];
  muscleFactors: MuscleFactors;
  auxOrder: number; /// used to make sure sort order is always deterministic
}

export interface MuscleCosts {
  overshoot?: number;
  undershoot?: number;
  undermax_factor?: number;
  overmax_factor?: number;
}
export interface ExerciseCosts {
  used?: number;
  sets?: number;
  sets_penalized?: number;
  used_penalized?: number;
  above4?: number;
  only1?: number;
}

export type ExerciseConstantsTable = {
  [exerciseId in ExerciseId]: ExerciseConstants;
};

export interface WorkoutSolverConstants {
  exercises: ExerciseConstantsTable;
  muscles: { [key in MuscleId]: MuscleConstants };
  customizationGroups: { [key in MuscleCustomizationGroup]: ExerciseId[] };

  avoids: ExerciseId[][];
  antagonistImportance: number;
  antagonists: ExerciseId[][];
  rebalances: ExerciseId[][];
  frequencies: {
    // what is set at [0] is defaults, overriden by specific frequency
    splitsPerLoop?: number;
    splitsPerLoopNovice?: number /** used if all main lifts are at novice level */;
    splitsPerLoopAdvanced?: number /** used if all main lifts are at advanced level */;
    exerciseSetsDailyMin?: number;
    exerciseSetsDailyMax?: number;
    workoutSetsMax?: number;
  }[];
  /**
   * Settings for the user-selectable training splits (#4591). Optional, because
   * a stored constants blob written before this feature existed will not carry
   * it, and Optimal — the only mode every existing user is on — never reads it.
   * The matching minimum training frequencies deliberately live elsewhere, in
   * `shared/src/workout-split-policy.ts`, because the client needs those too.
   */
  splitModes?: {
    /** Largest number of sets one workout may hold in a split mode. */
    workoutSetsMax: number;
    /** Largest number of sets one movement may carry in one workout, in a split mode. */
    exerciseSetsDailyMax: number;
    /** Rotation length by training days a week; index 0-2 unused, 0 means unavailable. */
    pushPullLegsRotation: number[];
    /** Rotation length by training days a week; index 0-1 unused, 0 means unavailable. */
    upperLowerRotation: number[];
  };
  /** Table of rules defining when some exercises are mandatory */
  mandatoryRules: {
    customizationMuscle?: MuscleCustomizationGroup /** if this applies only if some muscle is customized specially */;
    customizationType?: OneMuscleGrowthValue /** if this applies only if that muscle is customized in that way */;
    minFrequency?: number;
    mandatoryBy?: MandatoryBy;
    mandatoryWhen: MandatoryWhen;
    exercises: ExerciseId[] /** if all the rules above are satisfied, make all these mandatory */;
  }[];
  customizationTweaks: {
    [key in 'shrink' | 'maintain' | 'maximize']: {
      maxVolume: number;
      priorityModifier: number;
    }
  },
  costs: {
    rdlSquatUnbalanced: number;
    overmax: number;
    undermax: number;
    muscles: { [key: string]: MuscleCosts };
    exercises: { [key: string]: ExerciseCosts };
  };
}

export interface WorkoutSolverParams {
  // constants are provided from the client only during development/testing
  constants?: WorkoutSolverConstants;
  /**
   * Spread sets across more movements instead of stacking them onto one (#4591
   * follow-up). When true the solver caps any single movement at 6 sets in a
   * split-mode workout, and stops treating the two secondary squat patterns as
   * forbidden so that quads have somewhere to put their sets.
   *
   * The current app always sends true (#6769). Builds older than that resolved the
   * old `SolverSpreadSets` config switch and may send false or nothing, so absent or
   * false still means the solver behaves exactly as it did before.
   * See design-docs/training-splits.md.
   */
  spreadSetsAcrossMovements?: boolean;
  trainingLevels: TrainingLevels;
  immutableState: ImmutableState;
  declaredState: DeclaredState;
  fitnessState?: FitnessState; // for energy balance factor computation
  fitnessPlan?: FitnessPlan; // for energy balance factor computation
  energyBalanceFactor?: number; // for debugging only
  volumeOnlyMode?: boolean;
  fixedExercises?: { subSlotId: ExerciseId; dayIndex: number }[];
  muscleFactorOverrides?: { [subSlotId: string]: MuscleFactors };
  /**
   * For each sub-slot in `muscleFactorOverrides`, the slot in the exercise constants table its
   * muscle weights were copied from (`$hams`). The solver reads that slot's prime movers to
   * decide whether a Maintain or Shrink muscle makes a slotless exercise cost more (#5398).
   * Absent from older app versions, whose slotless exercises are then never penalised.
   */
  muscleFactorSourceSlots?: { [subSlotId: string]: string };
}

export interface WorkoutSolverExercise {
  subSlotId: ExerciseId;
  trainingLevel: TrainingLevel;
  sets: number;
  intensities: ExerciseIntensities;
}
/**
 * What kind of reply the workout solver gave (#5942).
 *
 * A string enum, not a boolean, because this code base compiles with `strict` off, and without
 * `strictNullChecks` TypeScript does not narrow a union on a boolean field. Same reason as
 * `MealTunerOutcome` and `PlanUpdateOutcome`.
 */
export enum WorkoutSolverOutcome {
  /** the solve ran; `days` is its answer, and an empty list is a real answer (no training days) */
  Solved = 'solved',
  /** our own solve failed, or the requested training split cannot be built; there is no answer */
  Failed = 'failed',
  /** the caller may not use the solver at all (a paid feature); there is no answer */
  Refused = 'refused',
}

/**
 * The refusal text sent to a user who is not on a paid plan. App versions before #5942 recognise
 * the refusal by finding "paid users only" in it and send the user to the Subscribe page, so the
 * wording must keep that phrase.
 */
export const WORKOUT_SOLVER_PAID_ONLY_MESSAGE = 'Feature available to paid users only.';

/** The parts of a solver reply that are there whatever the outcome. */
interface WorkoutSolverResultsCommon {
  debug: string;
  constantsText?: string;
}

export interface WorkoutSolverSolved extends WorkoutSolverResultsCommon {
  outcome: WorkoutSolverOutcome.Solved;
  days: WorkoutSolverExercise[][]; // array of days, which are arrays of exercises
  trainingLevels: TrainingLevels;
  trainingVolumeWeeklyAvg: number;
}

export interface WorkoutSolverFailed extends WorkoutSolverResultsCommon {
  outcome: WorkoutSolverOutcome.Failed;
  /** never empty; see the wire contract on `WorkoutSolverResults` */
  error: string;
}

export interface WorkoutSolverRefused extends WorkoutSolverResultsCommon {
  outcome: WorkoutSolverOutcome.Refused;
  /** never empty, and contains "paid users only"; see `WORKOUT_SOLVER_PAID_ONLY_MESSAGE` */
  error: string;
}

/**
 * The workout solver's reply. The answer fields exist only on the solved outcome, so code cannot
 * read a program without first checking `outcome` (#5942). The old flat shape let a caller that
 * forgot to check `error` read a crash as an empty program: the #5297 trap.
 *
 * Wire contract, because this reply crosses the network to app versions that can stay in use
 * indefinitely and that know nothing of `outcome`:
 * - every non-solved reply carries a present, NON-EMPTY `error`. Old apps test `error` before
 *   reading `days`, and a blank one would send them on to read a program that is not there.
 * - the refusal's `error` keeps containing "paid users only" (`WORKOUT_SOLVER_PAID_ONLY_MESSAGE`).
 * - a solved reply carries no `error`, which old apps read as empty, meaning success.
 *
 * The client reads every reply through `readWorkoutSolverReply`, which also understands a reply
 * from a server older than this shape.
 */
export type WorkoutSolverResults = WorkoutSolverSolved | WorkoutSolverFailed | WorkoutSolverRefused;

/** The message a failed or refused reply gets when it arrived without one. */
const BLANK_FAILURE_MESSAGE = 'The workout solver reported a failure with no description';

/**
 * Turn a reply as it arrived over the network into a `WorkoutSolverResults` (#5942).
 *
 * A reply from a server older than the `outcome` field has only `error`, so the outcome is derived
 * from it the way old apps read it: empty means solved, the paid-feature text means refused,
 * anything else means failed. A damaged reply (not an object, an unknown outcome, a failure with a
 * blank message) is read as failed with a message saying so, because it must never read as a
 * program.
 */
export function readWorkoutSolverReply(raw: unknown): WorkoutSolverResults {
  if (!raw || typeof raw !== 'object') {
    return { outcome: WorkoutSolverOutcome.Failed, error: `The workout solver's reply was not an object: ${String(raw)}`, debug: '' };
  }
  const reply = raw as { outcome?: unknown; error?: unknown; debug?: unknown; constantsText?: unknown };
  const error = typeof reply.error === 'string' ? reply.error : '';
  const common: WorkoutSolverResultsCommon = {
    debug: typeof reply.debug === 'string' ? reply.debug : '',
    constantsText: typeof reply.constantsText === 'string' ? reply.constantsText : undefined,
  };

  let outcome = reply.outcome;
  // if the server predates the outcome field, derive it the way old apps read `error`
  if (outcome === undefined) {
    if (!error) {
      outcome = WorkoutSolverOutcome.Solved;
    } else if (error.includes('paid users only')) {
      outcome = WorkoutSolverOutcome.Refused;
    } else {
      outcome = WorkoutSolverOutcome.Failed;
    }
  }

  switch (outcome) {
    case WorkoutSolverOutcome.Solved:
      // if a "solved" reply has no program, it is damaged; reading it as an empty program is
      // exactly the mistake this shape exists to prevent
      if (!Array.isArray((raw as { days?: unknown }).days)) {
        return { ...common, outcome: WorkoutSolverOutcome.Failed, error: 'The workout solver reported success but sent no program' };
      }
      return { ...(raw as WorkoutSolverSolved), outcome: WorkoutSolverOutcome.Solved };
    case WorkoutSolverOutcome.Refused:
      return { ...common, outcome: WorkoutSolverOutcome.Refused, error: error || WORKOUT_SOLVER_PAID_ONLY_MESSAGE };
    case WorkoutSolverOutcome.Failed:
      return { ...common, outcome: WorkoutSolverOutcome.Failed, error: error || BLANK_FAILURE_MESSAGE };
    default:
      return {
        ...common,
        outcome: WorkoutSolverOutcome.Failed,
        error: `The workout solver answered with an unknown outcome '${String(outcome)}'${error ? `: ${error}` : ''}`,
      };
  }
}

/** Whether a movement slot is trained at the user's upper-body or lower-body level. */
export type SlotLevelBy = 'upper' | 'lower';

/** Built on first use by `getSlotLevelByMap()` and never rebuilt. */
let slotLevelByCache: Readonly<Record<string, SlotLevelBy>> | undefined;

/**
 * The upper-or-lower mark for every movement slot, read off the one place it is
 * written down: the `levelBy` field of each entry in
 * `getExerciseConstantsTable()`. Derived rather than listed a second time so the
 * mapped type over `ExerciseId` is the only thing that has to be kept complete.
 *
 * Built once and frozen, and callers never receive the object — they go through
 * `getSlotLevelBy()`. The values are copied strings, so a caller mutating a
 * later `getExerciseConstantsTable()` result cannot disturb this.
 */
function getSlotLevelByMap(): Readonly<Record<string, SlotLevelBy>> {
  if (!slotLevelByCache) {
    const table = getExerciseConstantsTable();
    const map: Record<string, SlotLevelBy> = {};
    for (const slotId in table) {
      map[slotId] = table[slotId as ExerciseId].levelBy;
    }
    slotLevelByCache = Object.freeze(map);
  }
  return slotLevelByCache;
}

/**
 * Whether this slot is trained at the upper-body or the lower-body level.
 *
 * Takes a plain `string` and answers `undefined` for a slot the constants do
 * not know about, because that is what the callers actually face: a custom
 * exercise carried by `muscleFactorOverrides` is not in the constants table at
 * all. Every caller already handles the miss — this only makes the type say so.
 */
export function getSlotLevelBy(slotId: string): SlotLevelBy | undefined {
  return getSlotLevelByMap()[slotId];
}

/**
 * Work out whether a slot is a push, a pull or a leg movement (#4591), by
 * adding up how much of each split group its muscles carry. The heaviest group
 * wins.
 *
 * Nothing is tagged per exercise: the only new data is one mark per muscle. On
 * today's muscle weights this gives the conventional answer for every slot,
 * with no ties — see `design-docs/training-splits.md`.
 *
 * Two edge cases matter, and they are deliberately different from each other:
 *
 * - A **tie throws**. It cannot happen with today's weights, so a tie means
 *   somebody changed `muscleFactors` and the derived answer is no longer
 *   defined. Picking one silently would hide that.
 * - An **unknown slot returns `undefined`** and must not throw. A custom
 *   exercise carried by `muscleFactorOverrides` is not in the constants table
 *   at all. Those only ever occur when re-optimising a plan the user already
 *   has, which never reaches the day stage — so `undefined` means "not
 *   applicable here", and every caller must treat it that way.
 */
export function deriveSlotSplitGroup(
  slotId: string,
  exercises: { [id: string]: ExerciseConstants },
  muscles: { [key in MuscleId]?: MuscleConstants },
): SplitGroup | undefined {
  const exercise = exercises[slotId];
  if (!exercise) {
    return undefined;
  }

  const weightByGroup: { [key in SplitGroup]: number } = { push: 0, pull: 0, legs: 0 };
  for (const muscleId in exercise.muscleFactors) {
    const muscle = muscles[muscleId as MuscleId];
    if (!muscle) {
      throw new Error(`Cannot derive a split group for slot '${slotId}': it trains an unknown muscle '${muscleId}'.`);
    }
    if (!muscle.splitGroup) {
      // Says what is actually wrong. Without this the missing mark would land as
      // NaN under a key named "undefined" and come back as "carries no muscle
      // weight at all", which sends the reader looking in the wrong place.
      throw new Error(
        `Cannot derive a split group for slot '${slotId}': muscle '${muscleId}' has no splitGroup. ` +
          `These constants predate the training-split feature (#4591).`,
      );
    }
    weightByGroup[muscle.splitGroup] += exercise.muscleFactors[muscleId as MuscleId];
  }

  const ranked = (Object.keys(weightByGroup) as SplitGroup[]).sort((a, b) => weightByGroup[b] - weightByGroup[a]);
  const [heaviest, runnerUp] = ranked;
  if (weightByGroup[heaviest] === 0) {
    throw new Error(`Cannot derive a split group for slot '${slotId}': it carries no muscle weight at all.`);
  }
  if (weightByGroup[heaviest] === weightByGroup[runnerUp]) {
    throw new Error(
      `Cannot derive a split group for slot '${slotId}': '${heaviest}' and '${runnerUp}' both come to ` +
        `${weightByGroup[heaviest]}. Somebody changed the muscle weights and the answer is now ambiguous.`,
    );
  }
  return heaviest;
}

// Beware that this is a misnomer - those are not exercises but slots (for now - will be changed when we change the solver approach)
export function getExerciseConstantsTable(): ExerciseConstantsTable {
  return {
    $rdl: {
      kind: 'big',
      priorityAdjustment: -0.25,
      levelBy: 'lower',
      primeMovers: ['erectors'],
      muscleFactors: { utraps: 1, erectors: 1, hams: 1, glutes: 1, gastrocnemius: 0.25 },
      auxOrder: 1,
    },
    $squat: {
      kind: 'big',
      priorityAdjustment: 0.5,
      levelBy: 'lower',
      primeMovers: ['erectors'],
      muscleFactors: { erectors: 1, quads: 1, glutes: 1, soleus: 0.5 },
      auxOrder: 2,
    },
    $squat$1: {
      kind: 'big',
      priorityAdjustment: -0.5,
      levelBy: 'lower',
      primeMovers: ['erectors', 'quads'],
      muscleFactors: { erectors: 1, quads: 1, glutes: 1, soleus: 0.5 },
      auxOrder: 7,
    },
    $unilateral_squat: {
      kind: 'aux',
      priorityAdjustment: -0.25,
      levelBy: 'lower',
      primeMovers: ['quads'],
      muscleFactors: { erectors: 0.25, quads: 1, glutes: 1, soleus: 0.5 },
      auxOrder: 6,
    },
    $chin_up: {
      kind: 'big',
      priorityAdjustment: 0.5,
      levelBy: 'upper',
      primeMovers: [],
      muscleFactors: { rdelt: 1, lats: 1, biceps: 1 },
      auxOrder: 4,
    },
    $pull_up: {
      kind: 'big',
      priorityAdjustment: 0,
      levelBy: 'upper',
      primeMovers: ['lats'],
      muscleFactors: { rdelt: 0.5, lats: 1, biceps: 1 },
      auxOrder: 8,
    },
    $bench_press: {
      kind: 'big',
      priorityAdjustment: 1,
      levelBy: 'upper',
      primeMovers: [],
      muscleFactors: { chest: 1, triceps: 0.5 },
      auxOrder: 3,
    },
    $bench_press$1: {
      kind: 'big$2',
      priorityAdjustment: 0,
      levelBy: 'upper',
      primeMovers: ['chest'],
      muscleFactors: { chest: 1, triceps: 0.5 },
      auxOrder: 10,
    },
    $ohp: {
      kind: 'big',
      priorityAdjustment: 1.5,
      levelBy: 'upper',
      primeMovers: [],
      muscleFactors: { ldelt: 1, utraps: 0.25, triceps: 0.25 },
      auxOrder: 5,
    },
    $ohp$4: {
      kind: 'big$2',
      priorityAdjustment: 0,
      levelBy: 'upper',
      primeMovers: ['ldelt'],
      muscleFactors: { ldelt: 1, utraps: 0.25, triceps: 0.25 },
      auxOrder: 11,
    },
    $hip_extension: {
      kind: 'aux',
      priorityAdjustment: -0.5,
      levelBy: 'lower',
      primeMovers: ['glutes'],
      muscleFactors: { erectors: 0.5, hams: 1, glutes: 1 },
      auxOrder: 9,
    },
    $lateral$1: {
      kind: 'isolation',
      priorityAdjustment: -0.5,
      levelBy: 'upper',
      primeMovers: ['ldelt'],
      muscleFactors: { ldelt: 1, utraps: 0.25 },
      auxOrder: 13,
    },
    $lateral: {
      kind: 'isolation',
      priorityAdjustment: -0.5,
      levelBy: 'upper',
      primeMovers: ['ldelt'],
      muscleFactors: { ldelt: 1, utraps: 0.25 },
      auxOrder: 16,
    },
    $hams: {
      kind: 'isolation',
      priorityAdjustment: 0,
      levelBy: 'lower',
      primeMovers: ['hams'],
      muscleFactors: { hams: 1, gastrocnemius: 1 },
      auxOrder: 17,
    },
    $hams$2: {
      kind: 'isolation',
      priorityAdjustment: -1,
      levelBy: 'lower',
      primeMovers: ['hams'],
      muscleFactors: { hams: 1, gastrocnemius: 1 },
      auxOrder: 25,
    },
    $calves: {
      kind: 'isolation',
      priorityAdjustment: -1,
      levelBy: 'lower',
      primeMovers: ['gastrocnemius', 'soleus'],
      muscleFactors: { gastrocnemius: 1, soleus: 1 },
      auxOrder: 20,
    },
    $calves$4: {
      kind: 'isolation$2',
      priorityAdjustment: -2,
      levelBy: 'lower',
      primeMovers: ['gastrocnemius', 'soleus'],
      muscleFactors: { gastrocnemius: 1, soleus: 1 },
      auxOrder: 29,
    },
    $lats: {
      kind: 'isolation',
      priorityAdjustment: -0.25,
      levelBy: 'upper',
      primeMovers: ['lats'],
      muscleFactors: { rdelt: 1, lats: 1, triceps: 0.5 },
      auxOrder: 14,
    },
    $lats$3: {
      kind: 'isolation',
      priorityAdjustment: -1.25,
      levelBy: 'upper',
      primeMovers: ['lats'],
      muscleFactors: { rdelt: 1, lats: 1, triceps: 0.5 },
      auxOrder: 28,
    },
    $rear_delt: {
      kind: 'isolation',
      priorityAdjustment: -0.25,
      levelBy: 'upper',
      primeMovers: [],
      muscleFactors: { rdelt: 1 },
      auxOrder: 19,
    },
    $rear_delt$2: {
      kind: 'isolation$2',
      priorityAdjustment: -1.25,
      levelBy: 'upper',
      primeMovers: ['rdelt'],
      muscleFactors: { rdelt: 1 },
      auxOrder: 26,
    },
    $biceps: {
      kind: 'isolation',
      priorityAdjustment: 0,
      levelBy: 'upper',
      primeMovers: [],
      muscleFactors: { biceps: 1 },
      auxOrder: 22,
    },
    $biceps$4: {
      kind: 'isolation',
      priorityAdjustment: -1,
      levelBy: 'upper',
      primeMovers: ['biceps'],
      muscleFactors: { biceps: 1 },
      auxOrder: 30,
    },
    $quads: {
      kind: 'isolation',
      priorityAdjustment: 0,
      levelBy: 'lower',
      primeMovers: ['quads'],
      muscleFactors: { quads: 1 },
      auxOrder: 18,
    },
    $triceps: {
      kind: 'isolation',
      priorityAdjustment: 0,
      levelBy: 'upper',
      primeMovers: ['triceps'],
      muscleFactors: { triceps: 1 },
      auxOrder: 21,
    },
    $triceps$2: {
      kind: 'isolation',
      priorityAdjustment: -1,
      levelBy: 'upper',
      primeMovers: ['triceps'],
      muscleFactors: { triceps: 1 },
      auxOrder: 31,
    },
    $glutes: {
      kind: 'isolation',
      priorityAdjustment: 0.75,
      levelBy: 'lower',
      primeMovers: ['glutes'],
      muscleFactors: { glutes: 1 },
      auxOrder: 12,
    },
    $chest: {
      kind: 'isolation',
      priorityAdjustment: 0,
      levelBy: 'upper',
      primeMovers: ['chest'],
      muscleFactors: { chest: 1 },
      auxOrder: 15,
    },
    $chest$2: {
      kind: 'isolation$2',
      priorityAdjustment: -1,
      levelBy: 'upper',
      primeMovers: ['chest'],
      muscleFactors: { chest: 1 },
      auxOrder: 27,
    },
    $soleus: {
      kind: 'isolation',
      priorityAdjustment: 0,
      levelBy: 'lower',
      primeMovers: ['soleus'],
      muscleFactors: { soleus: 1 },
      auxOrder: 23,
    },
    $upper_traps: {
      kind: 'isolation',
      priorityAdjustment: 0,
      levelBy: 'upper',
      primeMovers: ['utraps'],
      muscleFactors: { utraps: 1 },
      auxOrder: 24,
    },
    $abs: {
      kind: 'isolation',
      priorityAdjustment: -1,
      levelBy: 'lower',
      primeMovers: ['abs'],
      muscleFactors: { abs: 1.5 },
      auxOrder: 25,
    },
    $glute_medius: {
      kind: 'isolation',
      priorityAdjustment: 1,
      levelBy: 'lower',
      primeMovers: ['glutemed'],
      muscleFactors: { glutemed: 1 },
      auxOrder: 32,
    },
    $glute_medius$2: {
      kind: 'isolation$2',
      priorityAdjustment: 1,
      levelBy: 'lower',
      primeMovers: ['glutemed'],
      muscleFactors: { glutemed: 1 },
      auxOrder: 33,
    },
  };
}
